import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Chip, createStyles, makeStyles, Tooltip } from '@material-ui/core';
import { createFile, verifyAddress } from 'api/file/file-api';
import { IVerifyAddressResponse } from 'api/file/interfaces/verify-address';
import { PREFERRED_OFFICE_TOOLTIP } from 'features/constants';
import { FileCreateContext, FileCreateContextInterface } from 'hooks/FileCreateContext';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { DefaultCreateFile } from 'models/create-file-model';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { getFilteredOfficesByPreference } from 'utilities/preferred-office-utils';
import { uniqueArray } from 'utilities/utilities';
import VerifiedPropertiesDrawer from '../file-property/VerifiedPropertiesDrawer';
import { addressVerifyCheckForMatch, apnVerifyCheckForMatch } from '../file-utilities';
import './FileCreateForm.scss';
import FileCreateFormWithBaseFormGroup from './FileCreateFormWithBaseFormGroup';

const useStyles = makeStyles((theme) =>
  createStyles({
    preferredOfficeChip: {
      height: 24,
      padding: theme.spacing(0, 1),
      borderRadius: 100,
      backgroundColor: theme.palette.brand.sky,
      color: theme.palette.primary.dark,
      fontSize: pxToRem(12),
      fontWeight: 400,
      lineHeight: '150%',
      border: 'none',
      '& .MuiChip-label': {
        padding: 0,
      },
    },
  }),
);

interface FileCreateContainerProps {
  openDrawer: boolean;
  setOpenDrawer: (isOpen: boolean) => void;
}

interface Sections {
  fileSection: React.MutableRefObject<null>;
  propertySection: React.MutableRefObject<null>;
  sellerSection: React.MutableRefObject<null>;
  buyerSection: React.MutableRefObject<null>;
  lenderSection: React.MutableRefObject<null>;
  secondBuyerSection: React.MutableRefObject<null>;
  secondSellerSection: React.MutableRefObject<null>;
}

const FileCreateContainer = ({ openDrawer, setOpenDrawer }: FileCreateContainerProps): JSX.Element => {
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const fileCreateCtx: FileCreateContextInterface = useContext(FileCreateContext) ?? {};
  const { fileCreateData, setFileCreateData } = fileCreateCtx;
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { setTabValue, loadFileOrderInsightsData, setSessionFileId } = fileDataCtx;
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, preferredOffice } = profileCtx;
  const classes = useStyles();
  const { showPreferredOfficeChip } = useMemo(
    () => getFilteredOfficesByPreference(preferredOffice, userFirm?.offices, userFirm?.firmId),
    [preferredOffice, userFirm?.offices, userFirm?.firmId],
  );
  const { addSnackbarMessage } = useSnackBars();
  const [showSaveFailedNotification, setShowSaveFailedNotification] = useState<boolean>();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const history = useHistory();
  const [fileCreateError, setFileCreateError] = useState<string[]>([]);
  const [savingFailedError, setSavingFailedError] = useState<{ msg: string[]; type: string }>({ msg: [], type: '' });

  const [verifiedPropertiesByIndex, setVerifiedPropertiesByIndex] = useState<Record<number, IVerifyAddressResponse[]>>(
    {},
  );

  const fileSection = useRef(null);
  const propertySection = useRef(null);
  const sellerSection = useRef(null);
  const buyerSection = useRef(null);
  const secondBuyerSection = useRef(null);
  const secondSellerSection = useRef(null);
  const lenderSection = useRef(null);
  const DEFAULT_TOASTER_TIMEOUT = 6000;

  const showToaster = useCallback(
    (notificationHook: (show: boolean) => void, duration: number = DEFAULT_TOASTER_TIMEOUT) => {
      notificationHook(true);
      changeValueWithDelay(notificationHook, duration, false);
    },
    [],
  );
  const changeValueWithDelay = useCallback(
    (notificationHook: (show: boolean) => void, duration: number = DEFAULT_TOASTER_TIMEOUT, value = false) => {
      setTimeout(() => {
        notificationHook(value);
      }, duration);
    },
    [],
  );
  const refsArray: Sections = {
    fileSection,
    propertySection,
    sellerSection,
    buyerSection,
    secondBuyerSection,
    secondSellerSection,
    lenderSection,
  };
  useEffect(() => {
    setSubmitAttempted(false);
    setFileCreateError([]);
  }, [openDrawer]);

  /** Submit data to create file */
  const submit = async (): Promise<string> => {
    /* remove the fileId field, so the backend will create a new one. */
    setSubmitAttempted(true);
    let fileId = '';
    if (isFormValid) {
      const token = await getAccessToken();
      const { lenders, buyers, sellers } = fileCreateData || {};
      lenders?.forEach((element) => {
        element.emailAddress = element.emailAddress?.trim() ?? '';
      });
      const cleanedFileInfo = {
        ...fileCreateData,
        propertyState: fileCreateData?.stateCode,
        firmId: +(userFirm?.firmId ?? 0),
        underwriterCode: `${fileCreateData?.underwriterCode ?? 0}`,
        buyers: buyers ? buyers.filter((buyer) => !!buyer.partyType) : [],
        sellers: sellers ? sellers.filter((sellers) => !!sellers.partyType) : [],
        lenders: lenders ? lenders.filter((lender) => !!lender.name) : [],
      };

      fileId = await createFile(cleanedFileInfo, token);
    }
    return fileId;
  };

  const verify = async (): Promise<IVerifyAddressResponse[]> => {
    /* This function is used to verify the address */
    setSubmitAttempted(true);
    let properties: IVerifyAddressResponse[] = [];
    if (isFormValid) {
      const token = await getAccessToken();

      if (fileCreateData?.properties && fileCreateData?.properties.length > 0) {
        properties = await verifyAddress(fileCreateData.properties[0], token);
      }
    }

    return properties;
  };

  const submitVerifiedProperty = () => {
    // The VerifiedPropertiesDrawer now handles property updates internally via FileDataContext
    // We just need to proceed with file creation after verification is complete
    handleCreateFile().then();
  };

  const resetDrawerData = () => {
    // Resetting all the data
    setFileCreateData && setFileCreateData({ ...DefaultCreateFile, stateCode: '' });
    setSubmitAttempted(false);
    setFileCreateError([]);
    setVerifiedPropertiesByIndex({});
  };

  const handleCancelCreate = () => {
    setOpenDrawer(false);
    resetDrawerData();
  };

  useEffect(() => {
    if (openDrawer) {
      resetDrawerData();
    }
  }, [openDrawer]);

  const {
    execute: handleCreateFile,
    value: createFileResults,
    status: createFileStatus,
    errors: createFileError,
  } = useAsync<string>(submit, false);

  const {
    execute: handleVerifyAddress,
    value: verifyAddressResults,
    status: verifyAddressStatus,
    errors: verifyAddressError,
  } = useAsync<IVerifyAddressResponse[]>(verify, false);

  useEffect(() => {
    if (createFileStatus === 'success') {
      /* It worked. Clear the data so next click get the defaults */
      setFileCreateData && setFileCreateData({ ...DefaultCreateFile, stateCode: '' });
    } else if (createFileStatus == 'error') {
      setIsSubmitted(false);
      if (Array.isArray(createFileError) && createFileError.length > 0)
        setFileCreateError(uniqueArray([...createFileError]));
    }
  }, [createFileStatus, createFileError]);

  useEffect(() => {
    if (createFileResults) {
      setSessionFileId?.(createFileResults ?? '');
      setOpenDrawer(false);
      setSubmitAttempted(false);
      setFileCreateError([]);
      setIsSubmitted(false);
      setVerifiedPropertiesByIndex({});
      loadFileOrderInsightsData && loadFileOrderInsightsData(createFileResults ?? '');
      /* Navigate to the file info page */
      history.push(`/files/${createFileResults}/file-info`);
    }
  }, [createFileResults]);

  useEffect(() => {
    fileCreateError?.map((err) => {
      addGlobalMsg({
        message: err,
        type: 'error',
      });
    });
  }, [fileCreateError]);

  useEffect(() => {
    if (verifyAddressStatus === 'success') {
      if (
        !verifyAddressResults ||
        Object.keys(verifyAddressResults).length === 0 ||
        verifyAddressResults.length === 0
      ) {
        addSnackbarMessage({
          html: true,
          title: 'Address Not Validated',
          message:
            'The address you entered could not be verified. Please check for errors or continue with the unverified address.',
          type: 'error',
        });

        handleCreateFile().then();
        return;
      } else {
        // If the address matches the first property, then we can skip the drawer and create the file directly
        if (verifyAddressResults.length === 1 && fileCreateData?.properties?.[0]) {
          if (fileCreateData.properties[0].propertyIdentificationMethod === 'apn') {
            if (apnVerifyCheckForMatch(fileCreateData.properties[0], verifyAddressResults[0])) {
              fileCreateData.properties[0].address1 = fileCreateData.properties[0].address1
                ? fileCreateData.properties[0].address1
                : verifyAddressResults[0].address1;
              fileCreateData.properties[0].address2 = fileCreateData.properties[0].address2
                ? fileCreateData.properties[0].address2
                : verifyAddressResults[0].address2;
              fileCreateData.properties[0].city = fileCreateData.properties[0].city
                ? fileCreateData.properties[0].city
                : verifyAddressResults[0].city;
              fileCreateData.properties[0].postalCode = fileCreateData.properties[0].postalCode
                ? fileCreateData.properties[0].postalCode
                : verifyAddressResults[0].zip;
              fileCreateData.properties[0].propertyDetailId = verifyAddressResults[0].propertyDetailId;
              handleCreateFile().then();
              return;
            }
          } else {
            if (addressVerifyCheckForMatch(fileCreateData.properties[0], verifyAddressResults[0])) {
              fileCreateData.properties[0].apn = verifyAddressResults[0].apn;
              fileCreateData.properties[0].propertyDetailId = verifyAddressResults[0].propertyDetailId;
              handleCreateFile().then();
              return;
            }
          }
        }

        verifyAddressResults.unshift({
          address1: fileCreateData?.properties?.[0]?.address1 ?? '',
          address2: fileCreateData?.properties?.[0]?.address2 ?? '',
          city: fileCreateData?.properties?.[0]?.city ?? '',
          stateCode: fileCreateData?.properties?.[0]?.state ?? '',
          zip: fileCreateData?.properties?.[0]?.postalCode ?? '',
          county: fileCreateData?.properties?.[0]?.county ?? '',
          apn: fileCreateData?.properties?.[0]?.apn ?? '',
          propertyDetailId: 0,
        });

        setVerifiedPropertiesByIndex({ 0: verifyAddressResults });
        setOpenDrawer(false);
        setIsSubmitted(false);
      }
    } else if (verifyAddressStatus == 'error') {
      setIsSubmitted(false);
      addSnackbarMessage({
        html: true,
        title: 'Address Not Validated',
        message:
          'The address you entered could not be verified. Please check for errors or continue with the unverified address.',
        type: 'error',
      });
      handleCreateFile().then();
    }
  }, [verifyAddressStatus, verifyAddressResults]);

  const handleScroll = (currentElement: any) => {
    if (currentElement) {
      currentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    let list = '';
    savingFailedError.msg.forEach((msg: string) => {
      if (msg) list += `<li>${msg}</li>`;
    });
    showSaveFailedNotification &&
      addSnackbarMessage({
        html: true,
        message: `<b class="savingFailedErrorHeading">Saving Failed</b>
            ${
              list
                ? `<ul class="savingFailedErrorList">
              ${list}
            </ul>`
                : ''
            }`,
        type: 'error',
        onClose: () => {
          setShowSaveFailedNotification(false);
        },
      });
  }, [showSaveFailedNotification]);

  return (
    <>
      <FormDrawerComponent
        title={'Create a File'}
        titleAdornment={
          showPreferredOfficeChip ? (
            <Tooltip title={PREFERRED_OFFICE_TOOLTIP} arrow>
              <Chip
                label="Preferred Offices Active"
                size="small"
                className={classes.preferredOfficeChip}
                data-testid="CreateFilePreferredOfficeChip"
              />
            </Tooltip>
          ) : undefined
        }
        open={openDrawer}
        primaryActionProps={{
          loading: isSubmitted,
          disabled: isSubmitted,
          'data-testid': 'CreateFileCreate',
          size: 'small',
        }}
        dismissActionProps={{
          disabled: isSubmitted,
          'data-testid': 'CreateFileCancel',
          size: 'small',
        }}
        crossQAPrefix="CreateFile"
        primaryActionLabel="Create & View File"
        onPrimaryAction={() => {
          // if (isFormValid) {
          //   sessionStorage.removeItem('fileId');
          //   setTabValue && setTabValue('0');
          //   setIsSubmitted(true);
          //   handleCreateFile().then();
          // } else {
          //   if (savingFailedError.type) handleScroll(refsArray[savingFailedError.type as keyof Sections]?.current);
          //   showToaster(setShowSaveFailedNotification);
          // }
          if (isFormValid) {
            setIsSubmitted(true);
            handleVerifyAddress().then();
          } else {
            setSubmitAttempted(true);
          }
        }}
        onDismissAction={handleCancelCreate}
        testId="scrollable-form-drawer"
        width={960}
      >
        {fileCreateData && setFileCreateData && (
          <>
            <FileCreateFormWithBaseFormGroup
              isFormValid={isFormValid}
              setIsFormValid={setIsFormValid}
              showAllValidation={submitAttempted}
              value={fileCreateData}
              onChange={setFileCreateData}
              setSavingFailedError={setSavingFailedError}
              refsArray={refsArray}
            />
          </>
        )}
      </FormDrawerComponent>

      {Object.keys(verifiedPropertiesByIndex).length > 0 && (
        <VerifiedPropertiesDrawer
          propertiesByIndex={verifiedPropertiesByIndex}
          sourcePage="create"
          submitAction={submitVerifiedProperty}
        />
      )}
    </>
  );
};

export default FileCreateContainer;
